import { autoinject } from 'aurelia-framework';
import { FrontendLogLevel } from '~/api/frontend-log-level';

import { Settings, SettingsApi } from '~/api/settings-api';

/**
 * SettingsState is a bunch of global variables related to:
 * - Global configs in Acurity, such as those defined in *.cfg files.
 * - User permissions, such as customisations
 */
@autoinject
export class SettingsState {
	/**
	 * state is retrieved after a user logs in.
	 */
	public state: Settings = {
		user: null,
		version: {
			versionNumber: '',
			buildDate: '',
			dbSchemaVersion: '',
			dbSchemaModified: '',
		},
		qasEnabled: false,
		usePostAddrLine1And2: false,
		useResAddrLine1And2: false,
		exportToCSVRowLimit: 0,
		frontendLogLevel: FrontendLogLevel.ERROR,
	};

	constructor(protected settingsApi: SettingsApi) {}

	/**
	 * fetchSettings, by default, is used to get both user and global and settings and is usually
	 * called when a user is logged in. But if parameter isUser is set to false, then only the global
	 * settings are fetched.
	 */
	public async fetchSettings(isUser: boolean = true) {
		let settings: Settings;
		try {
			settings = isUser
				? await this.settingsApi.getUser()
				: await this.settingsApi.getGlobalSettings();
		} catch (e) {
			throw e;
		}
		if (!settings) {
			throw new Error('User Settings is missing data.');
		}
		this.state = settings;
	}

	/**
	 * UNSAFE_modifyState is utilized by test code to mock/change this state.
	 * This should *NOT* be used elsewhere.
	 */
	public UNSAFE_modifyState(name: keyof Settings, value: any): void {
		(this.state as any)[name] = value;
	}

	/**
	 * getGlobalSettings is called before logging in and before rendering the first page.
	 */
	public async fetchGlobalSettings() {
		await this.fetchSettings(false);
	}
}
